import React, { lazy, Suspense, useEffect, useState, useCallback } from 'react';
import { ILink } from 'tbk-components/src/components/Button';
const DataTable = lazy(() => import('react-data-table-component'));
const DatePicker = lazy(() => import('react-datepicker'));
import { TableColumn, createTheme } from 'react-data-table-component';
import Button from 'tbk-components/src/components/Button';
import Icon from 'tbk-components/src/components/Icon';
import moment from 'moment';
import 'moment/locale/en-ca';
import 'moment/locale/fr-ca';
import { stringToDate, dateToString } from '.';
import RecruiterApplicantModal, { IRecruiterApplicantModalI18n, defaultI18n as defaultRecruiterApplicantModalI18n } from './RecruiterApplicantModal';

export interface IRecruiterDataTablePersonalSettings {
	columnOrder?: string[];
	hiddenColumns?: string[];
	sort?: {
		column: string;
		direction: string;
	};
	numberOfRows?: number;
}

export interface IRecruiterDataTableItem {
	first_name: string;
	last_name: string;
	job: ILink;
	date: string;
	email: string;
	phone: string;
	last_login: string;
	linkedin: string;
	has_profile: boolean;
	notification: boolean;
	quick_apply: boolean;
	full_apply: boolean;
	resume: ILink;
	cover_letter: ILink;
}

export interface IRecruiterDataTableI18n {
	columns?: {
		first_name?: string;
		last_name?: string;
		job?: string;
		date?: string;
		email?: string;
		phone?: string;
		last_login?: string;
		linkedin?: string;
		has_profile?: string;
		notification?: string;
		quick_apply?: string;
		full_apply?: string;
		resume?: string;
		cover_letter?: string;
	};
	searchPlaceholder?: string;
	download?: string;
	hideColumns?: string;
	recruiterApplicantModal?: IRecruiterApplicantModalI18n;
}

export interface IRecruiterDataTableProps {
	data: IRecruiterDataTableItem[];
	settings?: IRecruiterDataTablePersonalSettings;
	metric?: string;
	onChangePersonalSettings?: (
		settings: IRecruiterDataTablePersonalSettings,
	) => void;
	onClickDownload?: (args: any) => void;
	i18n?: IRecruiterDataTableI18n;
	selectedDate?: Date;
	fromDate?: Date;
	toDate?: Date;
}

const RecruiterDataTable: React.FC<IRecruiterDataTableProps> = ({
	data,
	settings,
	metric,
	onChangePersonalSettings,
	onClickDownload,
	i18n = {
		columns: {
			first_name: 'First Name',
			last_name: 'Last Name',
			job: 'Job',
			date: 'Date',
			email: 'Email',
			phone: 'Phone',
			last_login: 'Last Login',
			linkedin: 'LinkedIn',
			has_profile: 'Has Profile',
			notification: 'Notification',
			quick_apply: 'Quick Apply',
			full_apply: 'Full Apply',
			resume: 'Resume',
			cover_letter: 'Cover Letter',
		},
		searchPlaceholder: 'Search Name or Job',
		download: 'Download as Excel',
		hideColumns: 'Show/Hide Columns',
		recruiterApplicantModal: defaultRecruiterApplicantModalI18n,
	},
	selectedDate,
	fromDate,
	toDate,
}) => {
    const tableLanguage = document.documentElement.lang || 'en';
	if (tableLanguage === 'fr-ca') {
		moment.locale('fr-ca');
	} else {
		moment.locale('en-ca');
	}
	const [search, setSearch] = useState<string>();
	const [date, setDate] = useState<string>();
	useEffect( () => {
		setDate( dateToString(selectedDate) );
	}, [selectedDate]);
	const [showColumnSettings, setShowColumnSettings] =
		useState<boolean>(false);
	const [indexForApplicantModal, setIndexForApplicantModal] = useState<number>(null);
	const onClickHasProfile = useCallback( (row:IRecruiterDataTableItem) => {
		setIndexForApplicantModal(data.indexOf(row));
	},[data]);
	useEffect( () => {
		if ( showColumnSettings ) {
			const listener = (e) => {
				const target = e.target as HTMLElement;
				if ( ! target.closest('#column-settings') ) {
					setShowColumnSettings(false);
				}
			};
			document.body.addEventListener('click', listener);
			return () => {
				document.body.removeEventListener('click', listener);
			};
		}
	}, [showColumnSettings]);
	const column_keys = Object.keys(i18n.columns).filter( (key) => {
		switch ( metric ) {
			case 'registrations_per_day':
			case 'logins_per_day':
				// Exclude these columns as they don't make sense for these metrics
				return [ 'job', 'notification', 'quick_apply', 'full_apply' ].indexOf( key ) === -1;
			default:
				return true;
		}
	});
	const columns = column_keys.map((key) => {
		const column = {
			id: key,
			name: i18n.columns[key],
			selector: row => row[key]?.toLowerCase() || '',
			format: row => row[key],
			reorder: true,
			sortable: true,
			omit: ( settings?.hiddenColumns || [] ).indexOf(key) !== -1,
		} as TableColumn<unknown>;

		// Overrides for selectors and format
		switch (key) {
			case 'job':
			case 'resume':
			case 'cover_letter':
				column.selector = row => row[key]?.label?.toLowerCase() || '';
				column.format = row => {
					if ( ! row[key] ) {
						return null;
					} else if ( row[key].href ) {
						const props = {
							...row[key],
							label: row[key].icon ? null : row[key].label,
						}
						return <Button type="transparent" size="sm" classNames={['p-0']} {...props} target="_blank"/>;
					} else {
						return row[key].label;
					}
				};
				break;
			case 'has_profile':
			case 'notification':
			case 'quick_apply':
			case 'full_apply':
				column.selector = (row) => (row[key] ? 1 : 0);
				if ( 'has_profile' === key ) {
					column.format = (row) => {
						return row[key]
							? <Button type="transparent" size="sm" classNames={['p-0']} icon="check" onClick={() => onClickHasProfile(row as IRecruiterDataTableItem)}/>
							: null;
					};
				} else {
					column.format = (row) =>
						row[key] ? <Icon name="check" /> : null;
				}
				break;
			case 'date':
				column.format = (row) => moment(row[key]).format('MMM D');
				break;
			case 'last_login':
				 column.format = (row) => row[key] ? moment(row[key]).format('MMM D, YYYY') : '';
				break;
			case 'email':
			case 'linkedin':
				column.format = (row) => {
					const props = row[key] ? {
						href: 'email' === key ? `mailto:${row[key]}` : row[key] ,
						target: 'email' === key ? '' : '_blank',
						label: row[key],
					} : null;
					return row[key] ? <Button type="transparent" size="sm" classNames={['p-0']} {...props} /> : null;
				}
				break;
			default:
				break;
		}

		// Overrides for width
		switch (key) {
			case 'job':
				column.grow = 3;
				break;
			default:
				break;
		}
		return column;
	});

	if (settings?.columnOrder) {
		columns.sort((a, b) => {
			const _a = settings.columnOrder.indexOf(`${a.id}`) || 0;
			const _b = settings.columnOrder.indexOf(`${b.id}`) || 0;
			return _a - _b;
		});
	}

	const onColumnOrderChange = (next) => {
		if (onChangePersonalSettings) {
			onChangePersonalSettings({ columnOrder: next.map((c) => c.id) });
		}
	};
	const onSort = (column: TableColumn<unknown>, direction: string, sortedRows: unknown[]) => {
		if (onChangePersonalSettings) {
			onChangePersonalSettings({
				sort: { column: `${column.id}`, direction },
			});
		}
	};
	const onChangeRowsPerPage = (numberOfRows: number) => {
		if (onChangePersonalSettings) {
			onChangePersonalSettings({ numberOfRows });
		}
	};

	createTheme(
		'recruiter-dashboard',
		{
			text: {
				primary: '#2e2e2e',
				secondary: 'rgba(0, 0, 0, 0.54)',
				disabled: 'rgba(0, 0, 0, 0.38)',
			},
			background: {
				default: '#FFFFFF',
			},
			context: {
				background: '#e3f2fd',
				text: 'rgba(0, 0, 0, 0.87)',
			},
			divider: {
				default: 'rgba(0,0,0,.12)',
			},
			button: {
				default: 'rgba(0,0,0,.54)',
				focus: 'rgba(0,0,0,.12)',
				hover: 'rgba(0,0,0,.12)',
				disabled: 'rgba(0, 0, 0, .18)',
			},
			selected: {
				default: '#e3f2fd',
				text: 'rgba(0, 0, 0, 0.87)',
			},
			highlightOnHover: {
				default: '#0574b9',
				text: '#ffffff',
			},
			cells: {
				style: {
					paddingLeft: '32px',
					paddingRight: '32px',
					wordBreak: 'break-word',
				},
				draggingStyle: {},
			},
		},
		'light',
	);

	return (
		<Suspense fallback={null}>
			{data ? (
				<DataTable
					className="calian-recruiter-dashboard__data-table"
					theme="recruiter-dashboard"
					subHeader
					subHeaderComponent={
						<div className="calian-recruiter-dashboard__data-table--header w-100 text-end py-3">
							<label>
								<span className="visually-hidden">
									{i18n.searchPlaceholder}
								</span>
								<input
									type="text"
									className="form-control"
									placeholder={i18n.searchPlaceholder}
									value={search || ''}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</label>
							<label className="ms-2">
								<span className="visually-hidden">
									{i18n.columns.date}
								</span>
								<DatePicker
									selected={stringToDate(date)}
									onChange={(date: Date) =>
										setDate( dateToString(date) )
									}
									minDate={fromDate}
									maxDate={toDate}
									placeholderText={
										i18n.columns.date
									}
									isClearable
								/>
							</label>

							{onClickDownload && (
								<Button
									size="sm"
									className="btn btn-transparent btn-sm link-white px-3 ms-2 ps-0 pe-0"
									icon="download"
									type="transparent"
									title={i18n.download}
									onClick={() =>
										onClickDownload({ filter: search })
									}
								/>
							)}
							<div className="position-relative d-inline" id="column-settings">
								<Button
									className="btn btn-transparent btn-sm link-white px-3 ms-2 ps-0 pe-0"
									icon="funnel"
									type="transparent"
									title={i18n.hideColumns}
									onClick={() =>
										setShowColumnSettings((show) => !show)
									}
								/>
								{showColumnSettings && (
									<div
										className="position-absolute top-100 end-0 mt-3 bg-white fs-8"
										style={{ zIndex: 10 }}
									>
										<ul className="list-group text-start">
											{columns.map((column) => {
												return (
													<li
														className="list-group-item"
														key={column.id}
													>
														<label className="form-check-label text-nowrap">
															<input
																type="checkbox"
																className="form-check-input me-1"
																checked={
																	(
																		settings?.hiddenColumns ||
																		[]
																	).indexOf(
																		`${column.id}`,
																	) === -1
																}
																onChange={(
																	e,
																) => {
																	if (
																		onChangePersonalSettings
																	) {
																		onChangePersonalSettings(
																			{
																				hiddenColumns:
																					e
																						.target
																						.checked
																						? settings?.hiddenColumns?.filter(
																								(
																									c,
																								) =>
																									c !==
																									`${column.id}`,
																						  )
																						: [
																								...(settings?.hiddenColumns ||
																									[]),
																								`${column.id}`,
																						  ],
																			},
																		);
																	}
																}}
															/>{' '}
															{column.name}
														</label>
													</li>
												);
											})}
										</ul>
									</div>
								)}
							</div>
						</div>
					}
					// @ts-ignore
					subHeaderAlign="center"
					pagination
					onChangeRowsPerPage={onChangeRowsPerPage}
					paginationPerPage={settings?.numberOfRows}
					highlightOnHover
					responsive
					columns={columns}
					data={( search || date ) ? data.filter((row) => {
						let match = true;
						if (search) {
							match = false;
							['first_name', 'last_name', 'job', 'email'].forEach(
								(key) => {
									const value =
										'job' === key
											? row[key]?.label
											: row[key];
									if (
										value &&
										value
											.toLowerCase()
											.includes(search.toLowerCase())
									) {
										match = true;
									}
								},
							);
						}
						if ( date && row.date !== date ) {
							match = false;
						}
						return match;
					}) : data}
					onColumnOrderChange={onColumnOrderChange}
					onSort={onSort}
					sortFunction={(rows, selector, direction ) => {
						// This custom sort function is so that when sorting on a column that might have blanks in it, the blanks
						// are always sorted to the bottom of the list.
						const multiplier = 'desc' === direction ? -1 : 1;
						rows.sort( (a,b) => {
							const _a = selector(a);
							const _b = selector(b);
							if ( _a === '' && _b === '' ) {
								return 0;
							} else if ( _a === '' ) {
								return 1;
							} else if ( _b === '' ) {
								return -1;
							} else if ( _a < _b ) {
								return multiplier * -1;
							} else if ( _a > _b ) {
								return multiplier * 1;
							} else {
								return 0;
							}
						});
						return rows;
					}}
					defaultSortFieldId={settings?.sort?.column}
					defaultSortAsc={settings?.sort?.direction === 'asc'}
				/>
			) : null}
			{ ( indexForApplicantModal !== null )
				? <RecruiterApplicantModal
					applicant={data[indexForApplicantModal]}
					i18n={i18n.recruiterApplicantModal}
					onClose={() => setIndexForApplicantModal(null)}
				/>
				: null
			}
		</Suspense>
	);
};

export default RecruiterDataTable;
